// src/pages/NewsPage.jsx
import { FaCalendar, FaUser, FaArrowRight } from "react-icons/fa";

// Import blog images
import MarvelImg from "../assets/blog/marvel.jpg";
import CdlImg from "../assets/blog/cdl.jpg";

const articles = [
  {
    id: 1,
    title: "Prime Valor Dominates Caribbean 4v4 Tournament",
    excerpt: "Our CDL Team A secured a dominant victory in the recent Caribbean championship, showcasing exceptional teamwork and individual skill.",
    date: "January 28, 2025",
    author: "Prime Valor",
    category: "Tournament",
    image: CdlImg,
    featured: true,
  },
  {
    id: 2,
    title: "Marvel Rivals Division Announcement",
    excerpt: "Prime Valor is expanding into Marvel Rivals. We're looking for talented players to join our new competitive division.",
    date: "January 15, 2025",
    author: "Prime Valor",
    category: "Announcements",
    image: MarvelImg,
    featured: false,
  },
  {
    id: 3,
    title: "CDL Season 2 Preparations",
    excerpt: "Our CDL team is gearing up for the upcoming season with intense practice sessions and strategy development.",
    date: "December 20, 2024",
    author: "Prime Valor",
    category: "Team Updates",
    image: CdlImg,
    featured: false,
  },
];

function NewsPage() {
  const featuredArticle = articles.find(a => a.featured);
  const regularArticles = articles.filter(a => !a.featured);

  return (
    <main className="bg-black min-h-screen">
      {/* Hero Section */}
      <section className="relative py-24 px-6">
        <div className="absolute inset-0 bg-gradient-to-b from-red-900/20 to-black"></div>
        <div className="max-w-6xl mx-auto relative z-10">
          <h1 className="text-5xl md:text-7xl font-black uppercase tracking-tight text-white mb-6">
            News & <span className="text-red-600">Updates</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl">
            Stay informed with the latest news, tournament results, and announcements
            from Prime Valor.
          </p>
        </div>
      </section>

      {/* Featured Article */}
      {featuredArticle && (
        <section className="py-8 px-6">
          <div className="max-w-6xl mx-auto">
            <div className="group relative bg-gray-900 rounded-lg overflow-hidden border border-gray-800 hover:border-red-600/50 transition-all duration-300">
              <div className="grid md:grid-cols-2 gap-0">
                {/* Image */}
                <div className="aspect-video md:aspect-auto overflow-hidden">
                  <img
                    src={featuredArticle.image}
                    alt={featuredArticle.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>

                {/* Content */}
                <div className="p-8 flex flex-col justify-center">
                  <div className="mb-4">
                    <span className="px-3 py-1 bg-red-600 text-white text-xs font-bold uppercase rounded">
                      Featured
                    </span>
                    <span className="ml-2 text-red-500 text-sm uppercase tracking-wide">
                      {featuredArticle.category}
                    </span>
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold text-white mb-4 group-hover:text-red-500 transition-colors">
                    {featuredArticle.title}
                  </h2>
                  <p className="text-gray-400 mb-6">{featuredArticle.excerpt}</p>
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span className="flex items-center gap-2">
                      <FaCalendar className="text-red-600" />
                      {featuredArticle.date}
                    </span>
                    <span className="flex items-center gap-2">
                      <FaUser className="text-red-600" />
                      {featuredArticle.author}
                    </span>
                  </div>
                  <button className="mt-6 flex items-center gap-2 text-red-500 hover:text-red-400 font-semibold uppercase tracking-wide text-sm group/btn">
                    Read More
                    <FaArrowRight className="group-hover/btn:translate-x-1 transition-transform" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Articles Grid */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl font-bold text-white mb-8 uppercase border-l-4 border-red-600 pl-4">
            Latest Articles
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {regularArticles.map((article) => (
              <article
                key={article.id}
                className="group bg-gray-900 rounded-lg overflow-hidden border border-gray-800 hover:border-red-600/50 transition-all duration-300"
              >
                {/* Image */}
                <div className="aspect-video overflow-hidden">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>

                {/* Content */}
                <div className="p-6">
                  <div className="mb-3">
                    <span className="text-red-500 text-xs uppercase tracking-wide font-semibold">
                      {article.category}
                    </span>
                  </div>
                  <h3 className="text-lg font-bold text-white mb-3 group-hover:text-red-500 transition-colors line-clamp-2">
                    {article.title}
                  </h3>
                  <p className="text-gray-400 text-sm mb-4 line-clamp-2">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <FaCalendar className="text-red-600" />
                      {article.date}
                    </span>
                    <button className="flex items-center gap-1 text-red-500 hover:text-red-400 font-semibold uppercase tracking-wide group/btn">
                      Read
                      <FaArrowRight className="text-xs group-hover/btn:translate-x-1 transition-transform" />
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-16 px-6 bg-gradient-to-r from-red-900/20 to-gray-900">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Stay in the Loop
          </h2>
          <p className="text-gray-400 mb-8">
            Follow us on social media for the latest updates and behind-the-scenes content.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="https://discord.gg/SGXXCMpCxf"
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-bold uppercase tracking-wide rounded transition-colors"
            >
              Discord
            </a>
            <a
              href="https://www.tiktok.com/@primevaloresports"
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-3 bg-gray-800 hover:bg-gray-700 text-white font-bold uppercase tracking-wide rounded transition-colors"
            >
              TikTok
            </a>
            <a
              href="https://www.twitch.tv/primevaloresports"
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white font-bold uppercase tracking-wide rounded transition-colors"
            >
              Twitch
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}

export default NewsPage;
